import { useEffect, useRef, useState } from "react";
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polygon,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvent,
} from "react-leaflet";
import { DivIcon, DomEvent, LatLngBounds } from "leaflet";
import type { LeadSearchViewportBounds } from "@inrange/building-manager-api-client";

// Google Maps satellite + roads / labels stack. Internal-tool usage;
// the same URL pattern is fine across QA + prod (no referrer lock).
const TILE_URL = "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}";

export interface OverviewPin {
  id: string;
  lat: number;
  lon: number;
  label: string;
  // Optional GeoJSON-style coordinate array for the lead's chosen
  // building. 3-deep = Polygon, 4-deep = MultiPolygon (matches the
  // `BuildingCandidate.polygon` shape on the wire). When present we
  // render the polygon footprint instead of just a dot - far more
  // useful for spotting the right building on a satellite tile.
  polygon?: number[][][] | number[][][][] | null;
}

// The overview map's centre + zoom. The page keeps the latest one so it can
// restore the operator's view after they click into a result and back.
export interface MapViewport {
  center: [number, number];
  zoom: number;
}

// Handles two coupled behaviours on the overview map:
//
// 1. Fit-to-results on a typed-postcode search (when `fitToken` bumps).
//    The fit is programmatic and flagged so its `moveend` isn't mistaken
//    for a user pan. A viewport ("Search this area") search never bumps
//    `fitToken`, so the map stays where the operator panned.
// 2. Google-Maps-style "Search this area" button that appears ONLY when the
//    map has been moved away from what the current results reflect: hidden
//    right after any search completes (`hideToken` bumps), shown again on
//    the next user pan/zoom. `onSearchArea` omitted => button never shows.
const SearchAreaControl: React.FC<{
  pins: OverviewPin[];
  fitToken: number;
  hideToken: number;
  onSearchArea?: (bounds: LeadSearchViewportBounds) => void;
  searching: boolean;
  initialViewport: MapViewport | null;
  onViewportChange: (viewport: MapViewport) => void;
}> = ({
  pins,
  fitToken,
  hideToken,
  onSearchArea,
  searching,
  initialViewport,
  onViewportChange,
}) => {
  const map = useMap();
  const pinsRef = useRef(pins);
  pinsRef.current = pins;
  // True while WE are moving the map (fit / restore) so its moveend isn't
  // counted as a user pan (which would wrongly reveal the button). Every
  // programmatic move is animate:false, so its moveend fires SYNCHRONOUSLY
  // inside the move call - we set the flag, move, and clear it immediately,
  // never relying on a later moveend to clear it (the mount-restore move
  // fires its moveend before the listener below is even attached).
  const programmaticRef = useRef(false);
  const [show, setShow] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const moveProgrammatically = (apply: () => void) => {
    programmaticRef.current = true;
    apply();
    programmaticRef.current = false;
  };

  const fitToPins = () => {
    const current = pinsRef.current;
    if (current.length === 0) return;
    moveProgrammatically(() =>
      map.fitBounds(new LatLngBounds(current.map((p) => [p.lat, p.lon])), {
        padding: [40, 40],
        maxZoom: 14,
        animate: false,
      })
    );
  };

  // On (re)mount: restore the viewport the operator left when they clicked
  // into a result, so "back to map" returns to exactly where they were
  // (their panned/zoomed view), NOT a re-fit to all pins. On a fresh load
  // with no saved viewport, fall back to fitting the pins.
  const initialViewportRef = useRef(initialViewport);
  useEffect(() => {
    const iv = initialViewportRef.current;
    if (iv) {
      moveProgrammatically(() => map.setView(iv.center, iv.zoom, { animate: false }));
    } else {
      fitToPins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fit to the pins only when a NEW typed-postcode search completes
  // (fitToken changes) - guarded by a ref so it never fires on mount, which
  // would clobber the restored viewport above. A viewport ("Search this
  // area") search never bumps fitToken, so the map stays where panned.
  const fitTokenRef = useRef(fitToken);
  useEffect(() => {
    if (fitTokenRef.current === fitToken) return;
    fitTokenRef.current = fitToken;
    fitToPins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitToken]);

  // A completed search means the results now reflect the current view, so
  // hide the button until the operator moves the map again.
  useEffect(() => {
    setShow(false);
  }, [hideToken]);

  useMapEvent("moveend", () => {
    // Report the live viewport (pan + zoom) up so the page can restore it
    // when the operator clicks into a result and back. moveend fires on
    // zoom too, so this captures both.
    const c = map.getCenter();
    onViewportChange({ center: [c.lat, c.lng], zoom: map.getZoom() });
    // Only a user-initiated move reveals the button; a programmatic move has
    // the flag set (and, being synchronous, clears it right after).
    if (!programmaticRef.current) setShow(true);
  });

  // Disable click/scroll propagation to the map once the button mounts, so
  // pressing it doesn't pan or zoom underneath.
  useEffect(() => {
    if (wrapRef.current) {
      DomEvent.disableClickPropagation(wrapRef.current);
      DomEvent.disableScrollPropagation(wrapRef.current);
    }
  }, [show]);

  if (!onSearchArea || !show) return null;
  return (
    <div
      ref={wrapRef}
      style={{
        position: "absolute",
        top: 10,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 500,
      }}
    >
      <button
        type="button"
        disabled={searching}
        onClick={() => {
          const b = map.getBounds();
          onSearchArea({
            north: b.getNorth(),
            south: b.getSouth(),
            east: b.getEast(),
            west: b.getWest(),
          });
        }}
        style={{
          border: "none",
          borderRadius: 18,
          padding: "7px 16px",
          background: searching ? "#e9ecef" : "#0d6efd",
          color: searching ? "#868e96" : "#fff",
          fontSize: 13,
          fontWeight: 600,
          cursor: searching ? "default" : "pointer",
          boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
        }}
      >
        {searching ? "Searching..." : "Search this area"}
      </button>
    </div>
  );
};

// Convert a `BuildingCandidate.polygon` (Polygon or MultiPolygon
// coordinates) into the leaflet-shaped `LatLng[][]` arrays one
// `<Polygon />` per outer-ring stack. Returns one entry per polygon
// part; each entry is the rings of that polygon (outer + holes).
const buildingPolygonToLeafletRings = (
  poly: number[][][] | number[][][][] | null | undefined
): [number, number][][][] => {
  if (!poly || poly.length === 0) return [];
  const isMulti = Array.isArray((poly as number[][][][])?.[0]?.[0]?.[0]);
  if (isMulti) {
    return (poly as number[][][][]).map((p) => p.map(ringToLatLngs));
  }
  return [(poly as number[][][]).map(ringToLatLngs)];
};

// GeoJSON coords are [[lon, lat], ...]; leaflet wants [[lat, lon],
// ...]. Walks one ring; flips Polygon/MultiPolygon at the call site.
const ringToLatLngs = (ring: number[][]): [number, number][] =>
  ring.map(([lon, lat]) => [lat, lon] as [number, number]);

// A located lead (resolved building footprint) is drawn as an orange
// outline - the same orange the detail map gives the auto-selected /
// highest-confidence building (BUILDING_PALETTE[0] in LeadEditPanel), so the
// colour matches when you click through. A postcode-only lead (no footprint,
// only place-accurate) is a small blue dot.
const LOCATED_COLOUR = "#fd7e14";
const APPROX_COLOUR = "#0d6efd";

// A Google-Maps-style teardrop pin for the anchored ("Near this site") site,
// so it's obvious where the search was seeded from. The tip (iconAnchor,
// bottom-centre) sits on the exact coordinate; the label floats above it.
// Red with a white outline - distinct from the orange lead outlines and the
// blue approximate dots, and far more visible than the old black dot.
const ANCHOR_PIN_ICON = new DivIcon({
  className: "",
  html:
    '<svg width="28" height="38" viewBox="0 0 28 38" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M14 0C6.27 0 0 6.27 0 14c0 9.94 14 24 14 24s14-14.06 14-24C28 6.27 21.73 0 14 0z" ' +
    'fill="#e03131" stroke="#ffffff" stroke-width="2"/>' +
    '<circle cx="14" cy="14" r="5" fill="#ffffff"/></svg>',
  iconSize: [28, 38],
  iconAnchor: [14, 38],
  tooltipAnchor: [0, -32],
});

export const LeadsOverviewMap: React.FC<{
  pins: OverviewPin[];
  // The site the search was anchored on ("Near this site"), drawn as a
  // distinct, permanently-labelled reference marker so the operator can see
  // where they searched from. Deliberately does NOT affect the fit-to-results.
  anchorSite?: { lat: number; lon: number; name: string } | null;
  onPinClick?: (id: string) => void;
  // Bumped by the page when a typed-postcode search completes, to trigger a
  // one-shot fit-to-results. A viewport search leaves it unchanged so the
  // map stays where the operator panned. When omitted the map never
  // auto-fits (safe default).
  fitToken?: number;
  // Bumped by the page when ANY search completes, to hide the "Search this
  // area" button (the results now reflect the current view). The button
  // reappears on the next user pan/zoom.
  hideSearchAreaToken?: number;
  // "Search this area" handler - given the live map viewport bounds, run a
  // search over the outcodes it covers. When omitted the button is hidden.
  onSearchArea?: (bounds: LeadSearchViewportBounds) => void;
  // Disables the button + shows a "Searching..." label while a search is in
  // flight.
  searching?: boolean;
  // The viewport to restore on (re)mount - the operator's view from when they
  // last clicked into a result, so "back to map" returns there instead of
  // re-fitting. Null on first load (fit to pins instead).
  initialViewport?: MapViewport | null;
  // Called whenever the operator pans/zooms so the page can remember the
  // viewport to restore on the next mount.
  onViewportChange?: (viewport: MapViewport) => void;
}> = ({
  pins,
  anchorSite = null,
  onPinClick,
  fitToken = 0,
  hideSearchAreaToken = 0,
  onSearchArea,
  searching = false,
  initialViewport = null,
  onViewportChange,
}) => {
  const fallbackCentre: [number, number] = pins.length
    ? [pins[0].lat, pins[0].lon]
    : [54.5, -2.5]; // GB

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <MapContainer
        center={fallbackCentre}
        zoom={pins.length ? 12 : 6}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer attribution="© Google" url={TILE_URL} />
        <SearchAreaControl
          pins={pins}
          fitToken={fitToken}
          hideToken={hideSearchAreaToken}
          onSearchArea={onSearchArea}
          searching={searching}
          initialViewport={initialViewport}
          onViewportChange={onViewportChange ?? (() => {})}
        />
        {pins.flatMap((p) => {
          // Keep only parts with a real ring (>=3 vertices) so an empty /
          // malformed polygon falls through to the dot instead of rendering
          // an invisible, unclickable shape (which would hide the lead).
          const rings = buildingPolygonToLeafletRings(p.polygon).filter((part) =>
            part.some((ring) => ring.length >= 3)
          );
          // Located lead (resolved building footprint): render the outline
          // ONLY - no dot - with the tooltip + click on the boundary itself.
          // Each <Polygon> shares the lead id so clicking anywhere on the
          // footprint opens that lead.
          if (rings.length > 0) {
            return rings.map((part, i) => (
              <Polygon
                key={`lead-${p.id}-poly-${i}`}
                positions={part}
                pathOptions={{
                  color: LOCATED_COLOUR,
                  weight: 2,
                  opacity: 0.9,
                  fillColor: LOCATED_COLOUR,
                  fillOpacity: 0.35,
                }}
                eventHandlers={{ click: () => onPinClick?.(p.id) }}
              >
                <Tooltip>{p.label}</Tooltip>
              </Polygon>
            ));
          }
          // Postcode-only lead (no footprint): a small blue dot at the
          // place-accurate coordinate.
          return (
            <CircleMarker
              key={`lead-${p.id}-dot`}
              center={[p.lat, p.lon]}
              radius={5}
              pathOptions={{
                color: APPROX_COLOUR,
                fillColor: APPROX_COLOUR,
                fillOpacity: 0.6,
                weight: 2,
              }}
              eventHandlers={{ click: () => onPinClick?.(p.id) }}
            >
              <Tooltip>{p.label}</Tooltip>
            </CircleMarker>
          );
        })}
        {anchorSite && (
          // The anchored site: a Google-Maps-style red teardrop pin whose tip
          // points at the exact coordinate, with a permanent name label.
          // Non-interactive - it's a reference point, not a lead.
          <Marker
            position={[anchorSite.lat, anchorSite.lon]}
            icon={ANCHOR_PIN_ICON}
            interactive={false}
          >
            <Tooltip permanent direction="top">
              {anchorSite.name}
            </Tooltip>
          </Marker>
        )}
      </MapContainer>
      {pins.length === 0 && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "rgba(255,255,255,0.85)",
            padding: 12,
            borderRadius: 4,
            color: "#666",
            fontSize: 12,
          }}
        >
          No locations to show.
        </div>
      )}
    </div>
  );
};

export default LeadsOverviewMap;
